const registry = {};
const CONSOLE_WARN = console.warn.bind(console);
const MK_MOD = 'data-mod-name';
const MK_BYPASS = 'data-mod-bypass';
const MK_NOJS = 'data-mod-nojs';
const MK_NOCSS = 'data-mod-nocss';
const MK_INITED = 'data-inited';
const MK_INSTANT = 'data-mod-instant';
/**
 * Auto-initializes all modules on a page
 */
export default function autoInit(root = document, warn = CONSOLE_WARN, loader, assetPath, lazy = false) {
    /**
     * Add module's constructor to registry, indicating a module's code is loaded
     */
    const register = (moduleName, Ctor) => {
        if (typeof Ctor === 'function') {
            registry[moduleName] = Ctor;
        }
    };
    /**
     * Initialize and start a module
     */
    const initModule = (modName, node, warnFn = warn) => {
        const Ctor = registry[modName];
        if (typeof Ctor !== 'function') {
            throw new Error(`[autoInit] Could not find constructor in registry for ${modName}.`);
        }
        if (node.getAttribute(MK_INITED)) {
            warnFn(`[autoInit] Module already initialized for:`, node);
            return;
        }
        // equivalent to new()
        const module = Ctor.attachTo(node);
        // Mark the node, see unit-test
        Object.defineProperty(node, modName, {
            configurable: true,
            enumerable: false,
            value: module,
            writable: false
        });
        node.setAttribute(MK_INITED, 'true');
        node.setAttribute(`data-${modName}-init`, 'true'); // backward compatibility
        if (typeof module.publish === 'function') {
            module.publish('mod:init', { moduleName: modName });
        }
        if (typeof module.start === 'function') {
            // *really* start a module
            module.start();
        }
    };
    /**
     * Load (import) the module's JS code if not already
     */
    const loadModuleJs = (node, moduleName) => {
        const modName = moduleName ?? node.getAttribute(MK_MOD);
        if (!modName) {
            throw new Error('[autoInit] Constructor name must be given.');
        }
        if (!node.hasAttribute(MK_NOJS)) {
            if (!registry[modName]) {
                // load module JS on demand
                if (!assetPath) {
                    throw new Error('Ctor not registered and unable to be loaded since assets path missing!');
                }
                // Load module JS file per naming convention
                const moduleFile = `${assetPath.modules}/mod-${modName}/${modName}.js`;
                try {
                    import(moduleFile).then((mod) => {
                        register(modName, mod.default);
                        initModule(modName, node, warn);
                    }).catch((err) => {
                        console.error(err);
                        throw new Error(err);
                    });
                }
                catch (err) {
                    console.error('Dynamic importing not supported!');
                }
            }
            else {
                // Module already registered
                initModule(modName, node, warn);
            }
        }
    };
    // options.threshold added as fallback in case observer isn't fired immediately the node is shown.
    const options = { threshold: 0 };
    const observer = new IntersectionObserver(function (nodes, observer) {
        nodes.forEach((node) => {
            if (node.isIntersecting) {
                loadModuleJs(node.target);
                observer.unobserve(node.target);
            }
        });
    }, options);
    // find all marked elements
    const nodes = root.querySelectorAll(`[${MK_MOD}]`);
    for (let i = 0, node; (node = nodes[i]); i++) {
        if (node.hasAttribute(MK_BYPASS)) {
            continue;
        }
        const modName = node.getAttribute(MK_MOD);
        if (!modName) {
            throw new Error('[autoInit] Constructor name must be given.');
        }
        if (!node.hasAttribute(MK_NOCSS)) {
            // Load CSS file per naming convention
            if (loader && assetPath) {
                const cssFilePath = `${assetPath.modules}/mod-${modName}/${modName}.css`;
                loader.loadCSS(cssFilePath);
            }
        }
        const instant = node.hasAttribute(MK_INSTANT); // some modules may need to be initialized immediately
        if ((!lazy) || instant) {
            loadModuleJs(node, modName);
        }
        else {
            // Load module's JS when they are visible
            observer.observe(node);
        }
    }
}
/**
 * Map module name to the corresponding class
 */
autoInit.register = function (moduleName, Ctor, warn = CONSOLE_WARN) {
    if (typeof Ctor !== 'function') {
        throw new Error(`[autoInit] Invalid Ctor value ${Ctor}. Expected function.`);
    }
    if (registry[moduleName]) {
        warn(`[autoInit] Overriding registration for "${moduleName}".`);
    }
    registry[moduleName] = Ctor;
};
autoInit.deregister = function (moduleName) {
    delete registry[moduleName];
};
autoInit.deregisterAll = function () {
    Object.keys(registry).forEach(this.deregister, this);
};
